import { createContext, useContext } from 'react';

import type { TourMachineState, EndReason } from './engine/machine';
import type { TourProgress } from './progress/use-tour-progress';
import type { TourRegistry } from './registry';
import type { TourAccess, TourCardStatus, TourDefinition, TourSource, TourStep } from './types';

export interface StartOptions {
  source: TourSource;
}

export interface TourContextValue {
  registry: TourRegistry;
  access: TourAccess;
  /** i18n namespace holding tour copy. */
  namespace: string;
  /** Interpolation values for tour copy, e.g. `{ firstName }`. */
  values: Record<string, string>;
  /** Kill switch from runtime config, two-factor gate. False hides every entry point. */
  enabled: boolean;
  helpPath: string;
  welcomeTourId: string;
  storeId: string | undefined;
  state: TourMachineState;
  /** The element the current step points at, once found. */
  element: HTMLElement | null;
  isMobile: boolean;
  reducedMotion: boolean;
  progress: TourProgress;
  isAvailable: (definition: TourDefinition) => boolean;
  statusOf: (definition: TourDefinition) => TourCardStatus;
  stepsFor: (definition: TourDefinition) => TourStep[];
  /** Resolves false when the tour cannot run (unknown, not permitted, no store or record). */
  start: (tourId: string, options: StartOptions) => Promise<boolean>;
  next: () => void;
  back: () => void;
  end: (reason: EndReason) => void;
  resume: () => void;
  /** Saves the tour as dismissed without running it ("Don't show again"). */
  dismissWithoutRunning: (tourId: string) => void;
  /** Speaks a message through the tour's polite live region. */
  announce: (message: string) => void;
  announcement: string;
  /** Route params of the record a record tour runs on; null when there is none. */
  resolveRecord: (definition: TourDefinition) => Promise<Record<string, string> | null>;
}

export const TourContext = createContext<TourContextValue | null>(null);

/** Inside a `TourProvider`; throws elsewhere. */
export function useTour(): TourContextValue {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTour must be used inside a TourProvider');
  return value;
}

/** Null outside a provider, so shared components (PageHeader, EmptyState) render without it. */
export function useTourOptional(): TourContextValue | null {
  return useContext(TourContext);
}
